import { match, P } from 'ts-pattern';
import Logger from "@utils/logger";
import { OperationElement } from "@context/map/fields/fieldsStore";
import { Crop } from "@baseTypes/baseType";
import { UnitElementDynamic } from "@context/map/units/unitsStore";

type WsMessage = {
    isTrusted: boolean,
    data: AsEventMessage
}
type MessageTime = number

interface ChangeValues {
}

export interface CultivationInfo {
    clear: boolean
    base?: OperationElement[]
    insert?: OperationElement[]
    operationId?: number
}
export interface CropsInfo { cropData: Crop[] }

export interface CropInfoChange extends ChangeValues {
    cropsInfo: CropsInfo
}
export interface CultivationInfoChange extends ChangeValues {
    calcStatus: number,
    cultivationInfo: CultivationInfo
}

interface RealTimeChange extends ChangeValues {
    rt_position: [number, number],
    rt_time: MessageTime
}

export type ChangeKey = "position" | any
export type WsUnitChange = UnitElementDynamic
export type WsGeoZoneChange = CultivationInfoChange & CropInfoChange
export type WsChangeBody = RealTimeChange & { [key in ChangeKey]: any } & WsGeoZoneChange & WsUnitChange
export type EventType = "changes" | "youAre" | undefined

export type WsChange = {
    id: number,
    time: number,
    values: WsChangeBody
}
type EventMessageBody = {
    items: WsChange[]
}
type AsEventMessage = {
    event: EventType,
    data: EventMessageBody
}

type InitialMessage = {
    data: {
        data: {
            demo: boolean
            id: string
        }
        event: EventType
    }
    isTrusted: boolean
}

const log = Logger.getLogger("ws.service")

interface WebSocketServiceProps {
    optionsConnect: () => any
    initialRequests: any
    onReady: (ws: WebSocketService) => any
}

type WsChangeAccumulator = { "FIELDS": WsChange[], "UNITS" : WsChange[] }
type WsChangeListener =  (change: WsChange[]) => any
class WebSocketService {
    customListeners: { [k in keyof WsChangeAccumulator]? :WsChangeListener } = {}  //{name, customListeners}
    errorListeners: any = {}
    private connectionId: string | undefined = undefined
    private timer: any
    private ws: any
    private incrementReconnected: number = 0
    private sendFirstMessage: boolean = true

    constructor(props: WebSocketServiceProps) {
        let {
            optionsConnect,
            initialRequests,
            onReady
        } = props

        this.ws = this.connect(optionsConnect())
        this.ws.onopen = (e: any) => {
            log.debug('Ws connection established')
            clearInterval(this.timer)
        }
        this.ws.onclose = (e: any) => {
            log.debug('Ws connection closed')
        }
        this.ws.onmessage = (message: any) => {
            let messageCopy = {
                ...message,
                data: JSON.parse(message.data)
            }
            if (this.sendFirstMessage) {
                let initialMessage = messageCopy as InitialMessage
                log.debug("Got initial message", initialMessage)
                match(initialMessage)
                    .with({ data: { data: { id: P.string } } }, (message) => {
                        this.connectionId = message.data.data.id
                        log.info("Connection id obtained", this.connectionId)
                        let arrayRequest: any = initialRequests(messageCopy)
                        arrayRequest.forEach((request: any) => this.ws.send(JSON.stringify(request)))
                        onReady(this)
                        this.sendFirstMessage = false
                    })
                    .with(P._, () => log.error("The initial message has wrong format...", initialMessage))
                    .exhaustive()
            } else {
                this.getMessage(messageCopy)
            }
        }
        this.ws.onerror = (e: any) => {
            log.error("Unexpected error while connecting to websoket", JSON.stringify(e))
            log.warn("Ws connection couldn't be established")
            // if (this.incrementReconnected < 5) {
            //     this.timer = setInterval(() => {
            //         this.connect(optionsConnect());
            //         this.incrementReconnected++
            //     }, 5000)
            // } else {
            //     this.reset()
            //     clearInterval(this.timer)
            // }
        }

        this.close = this.close.bind(this)
        this.send = this.send.bind(this)
        this.reset = this.reset.bind(this)
        this.getMessage = this.getMessage.bind(this)
        this.getError = this.getError.bind(this)
        this.setListenerMessage = this.setListenerMessage.bind(this)
        this.setListenerError = this.setListenerError.bind(this)
    }

    //==================================================================================>
    public close() {
        log.debug("Closing connection...", this.ws)
        this.ws.close()
    }

    public sendMessage(event: string, data: any) {
        log.debug("Sending message to websocket", event, data)
        this.ensureConnection((cid: string) =>
            this.ws.send(JSON.stringify({
                cid,
                data,
                event
            })))
    }

    private ensureConnection<T>(fn: (connectionId: string) => T) {
        if (this.connectionId) {
            return fn(this.connectionId)
        } else throw Error('Connection is empty. It looks like ws connection isn\'t established')
    }

    public send(data: any) {
        log.debug("Sending message to websocket", data)
        this.ws.send(JSON.stringify(data))
    }

    public setListenerMessage(type: keyof WsChangeAccumulator, listener: any) {
        let copyListener = this.customListeners[type]
        if (!copyListener) {
            this.customListeners[type] = listener
        }
    }

    public setListenerError(type: string, listener: any) {
        let copyListener = this.errorListeners[type]
        if (!copyListener) {
            this.errorListeners[type] = listener
        }
    }

    getMessage(message: WsMessage) {
        //определение к какому типу относится сообщени
        let accumulatedMessage = message.data.data.items.reduce((accum: WsChangeAccumulator, change: WsChange) => {
            if (
                (change.values.hasOwnProperty("rt_position") ||
                    change.values.hasOwnProperty("track") ||
                    change.values.hasOwnProperty("syncWithMeteostation") ||
                    change.values.hasOwnProperty("unit") ||
                    change.values.hasOwnProperty("unitState"))
            ) {

                return {
                    ...accum,
                    "UNITS": [...accum.UNITS, change]
                }
            }
            return match(change.values)
                // .with({ rt_position: [P.number, P.number] }, () => console.log("RT position"))
                // .with({ operation: P.any }, (x) => console.log("Got operation", x))
                .with({ cultivationInfo: P.any }, { cropsInfo: P.any }, (x) => {
                    return ({ ...accum, "FIELDS": [...accum.FIELDS, change] })
                })
                .with(P._, () => accum)
                .exhaustive()

        }, {
            "UNITS": [],
            "FIELDS": []
        })
        // log.debug('Meta message', metaMessage)
        let name: keyof WsChangeAccumulator;
        for (name in this.customListeners) {
            let listener = this.customListeners[name]
            if(listener) {
                let meta = accumulatedMessage[name]
                if (meta) listener(meta)
            }
        }
    }

    getError(error: any) {

        //определение к какому типу относится сообщение
        let metaError = ""

        for (let listener in this.errorListeners) {
            if (listener === metaError) {
                this.errorListeners[listener](error)
            }
        }
    }

    private connect = (options: any) => {
        let {
            url,
            token,
            serverId,
            companyId,
            departmentId
        } = options

        const urlCopy = `${url}?jwt=Bearer%20${token}${serverId ? `&serverId=${serverId}` : ''}${companyId ? `&companyId=${companyId}` : ''}${departmentId ? `&departmentId=${departmentId}` : ''}`
        return new WebSocket(urlCopy)
    }

    private reset() {
        this.close()
        this.ws = {}
        this.incrementReconnected = 0
        this.sendFirstMessage = true
    }


}

export default WebSocketService